/**
 * เงื่อนไขการขนส่ง: the terms a sender agrees to, set in code rather than in the app (owner, 1 Nov 2569). Each version
 * takes effect on a day and is numbered by that month (`T6911-0001`); a change is a new version added at the end, never
 * an edit of one bills keep. A bill or job keeps the version in effect on its day (`termsId` holds its number, from
 * `termsOn` on submit), so it prints, and its tracking page shows, what its sender agreed to, however the terms change
 * after.
 *
 * The customer's copy prints `summary` (a point a line) above the sender's signature, which agrees to them in so many
 * words: a clause limiting the carrier's liability binds only a sender who agreed to it expressly (ป.พ.พ. ม.625). The
 * whole text (`body`) is on the bill's tracking page, behind the QR on that copy, and on the company website.
 */
export interface TermsVersion {
  /** `T`, the Buddhist year and month it takes effect, a running number: what bills keep and print */
  number: string
  effectiveFrom: string
  /** Printed on the customer's copy: a point a line, a few */
  summary: string
  body: string
}

/**
 * The versions, oldest first. The first was researched 30 Oct 2569 from the published terms of Thai carriers: at most
 * 2,000 baht a bill for loss or damage, as most of them pay, and no cover beyond it — the owner chose not to sell
 * coverage or insurance (30 Oct 2569), so a sender of dearer goods insures them; what is not carried; packing; claims
 * within the 8 days ป.พ.พ. ม.623 allows for hidden damage; goods nobody collects (ม.631). The limit binds only a sender
 * who agreed to it expressly (ม.625): the customer's copy prints it above their signature.
 */
export const termsVersions: readonly TermsVersion[] = [
  {
    number: 'T6911-0001',
    effectiveFrom: '2026-11-01',
    summary: [
      'ไม่รับเงินสด ทองคำ ของมีค่า เอกสารสำคัญ สัตว์มีชีวิต วัตถุอันตราย และของผิดกฎหมาย',
      'ผู้ส่งแจ้งชนิดสินค้าตามจริง และบรรจุหีบห่อให้แข็งแรงพอสำหรับการขนส่ง',
      'เสียหายหรือสูญหาย บริษัทชดใช้ตามจริงไม่เกิน 2,000 บาทต่อบิล ไม่มีบริการประกันสินค้า',
      'ของแตกง่าย ของเหลว ของใช้แล้ว ที่บรรจุไม่เหมาะสม บริษัทรับผิดเฉพาะกรณีสูญหาย',
      'ตรวจสินค้าก่อนเซ็นรับ ความเสียหายที่มองไม่เห็นจากภายนอก แจ้งภายใน 7 วัน',
      'ไม่มีผู้รับ บริษัทแจ้งผู้ส่ง ไม่มีคำสั่งใน 30 วัน ส่งคืนโดยผู้ส่งจ่ายค่าส่งขากลับ',
    ].join('\n'),
    body: `1. ขอบเขต
เงื่อนไขนี้ใช้กับการรับขนสินค้าทุกบิลและทุกงานของบริษัท ผู้ส่งตกลงตามเงื่อนไขนี้โดยลงชื่อในบิล หรือตกลงไว้ในใบเสนอราคาหรือสัญญากับบริษัท

2. สินค้าที่ไม่รับขนส่ง
เงินสด ธนบัตร ตั๋วเงิน ทองคำ เครื่องประดับ อัญมณี ของสะสมหรือของมีค่าอื่น เอกสารสำคัญ สัตว์มีชีวิต ศพหรืออวัยวะ วัตถุไวไฟ วัตถุระเบิด สารเคมีหรือวัตถุอันตราย อาวุธ ยาเสพติด สินค้าละเมิดลิขสิทธิ์ และสินค้าที่กฎหมายห้ามขนส่ง ถ้าพบภายหลัง บริษัทระงับการขนส่ง แจ้งผู้ส่ง และส่งมอบให้เจ้าหน้าที่เมื่อกฎหมายกำหนด โดยไม่รับผิดต่อความเสียหายของสินค้านั้น

3. หน้าที่ของผู้ส่ง
ผู้ส่งแจ้งชนิด จำนวน น้ำหนัก และมูลค่าของสินค้าตามจริง และบรรจุหีบห่อให้แข็งแรงเหมาะกับชนิดสินค้าและการขนส่งทางรถ ของแตกง่ายต้องกันกระแทกและติดป้ายระวังแตก บริษัทอาจขอตรวจดูสินค้า หรือปฏิเสธการรับขนสินค้าที่บรรจุไม่เหมาะสม

4. ความรับผิดของบริษัท
สินค้าเสียหายหรือสูญหายระหว่างที่อยู่ในความดูแลของบริษัท บริษัทชดใช้ตามความเสียหายจริง ไม่เกิน 2,000 บาทต่อบิล บริษัทไม่รับผิดเมื่อความเสียหายเกิดจากเหตุสุดวิสัย จากสภาพของสินค้าเอง หรือจากความผิดของผู้ส่งหรือผู้รับ เช่น บรรจุหีบห่อไม่เหมาะสม หรือแจ้งชนิดสินค้าไม่ตรงความจริง
ของแตกง่าย ของเหลว เครื่องใช้ไฟฟ้า และของใช้แล้ว ที่บรรจุไม่เหมาะสม บริษัทรับผิดเฉพาะกรณีสูญหายทั้งชิ้น

5. ไม่มีบริการประกันสินค้า
บริษัทรับผิดไม่เกิน 2,000 บาทต่อบิลตามข้อ 4 ไม่ว่าสินค้าจะมีมูลค่าเท่าใด และไม่มีบริการประกันสินค้าหรือการซื้อความคุ้มครองเพิ่ม มูลค่าสินค้าที่ผู้ส่งแจ้งในบิลเป็นข้อมูลเท่านั้น ไม่เพิ่มวงเงินความรับผิดของบริษัท สินค้ามูลค่าเกิน 2,000 บาท ผู้ส่งควรทำประกันภัยสินค้ากับบริษัทประกันภัยเอง

6. การรับสินค้าและการแจ้งความเสียหาย
ผู้รับตรวจสภาพและจำนวนสินค้าต่อหน้าพนักงานก่อนลงชื่อรับ ความเสียหายที่เห็นได้จากภายนอกให้บันทึกไว้ตอนรับ ความเสียหายที่มองไม่เห็นจากภายนอก แจ้งบริษัทภายใน 7 วันนับจากวันที่รับสินค้า พร้อมเลขที่บิล รูปถ่ายสินค้าและหีบห่อ และหลักฐานมูลค่าสินค้า เช่น ใบกำกับภาษีหรือใบเสร็จการซื้อ สินค้าสูญหายแจ้งได้เมื่อพ้นกำหนดส่งแล้ว บริษัทพิจารณาและแจ้งผลภายใน 30 วันนับจากได้รับเอกสารครบ

7. สินค้าที่ไม่มีผู้รับ
ผู้รับไม่อยู่ ปฏิเสธการรับ หรือไม่มารับที่สาขาภายใน 7 วัน บริษัทแจ้งผู้ส่งเพื่อขอคำสั่ง ถ้าไม่ได้รับคำสั่งภายใน 30 วันนับจากวันที่แจ้ง บริษัทส่งสินค้าคืนผู้ส่ง และผู้ส่งชำระค่าขนส่งขากลับ สินค้าที่เน่าเสียได้ บริษัทดำเนินการตามที่กฎหมายกำหนด

8. ค่าขนส่งและค่าบริการ
ค่าขนส่งและค่าบริการเสริมชำระตามที่ระบุในบิล โดยผู้ส่งหรือผู้รับตามที่ตกลง ผู้ส่งยังต้องรับผิดชอบค่าขนส่งที่ผู้รับไม่ชำระ

9. การเปลี่ยนเงื่อนไข
บริษัทอาจออกเงื่อนไขฉบับใหม่ได้ บิลแต่ละใบใช้ฉบับที่มีผลในวันที่ฝากส่ง ซึ่งพิมพ์เลขที่ฉบับไว้ในบิล`,
  },
]

/** The version in effect on `date` (YYYY-MM-DD): the latest to take effect by then; none before the first */
export const termsOn = (date: string) => termsVersions.filter((version) => version.effectiveFrom <= date).pop()

/** A version by its number, as a bill or job keeps it (`termsId`) */
export const termsVersion = (number: unknown) => termsVersions.find((version) => version.number === number)

/** A version as the website shows it to anyone: what it says, from when, and whether it is in effect today */
export interface PublicTerms {
  number: string
  effectiveFrom: string
  /** The key points, each in parts with its figures marked (`termsParts`), for the page to set them bold and underlined */
  summary: { text: string; figure: boolean }[][]
  body: string
  current: boolean
}

/** The website's reply for a version, field by field */
export const publicTermsOf = (version: TermsVersion, current: boolean): PublicTerms => ({
  number: version.number,
  effectiveFrom: version.effectiveFrom,
  summary: termsPoints(version.summary).map(termsParts),
  body: version.body,
  current,
})

/** A figure in a key point, with the unit after it: "2,000 บาท", "7 วัน", "30 วัน" */
const FIGURE = /(\d[\d,.]*(?:\s*(?:บาท|วัน|ชั่วโมง|เดือน|ปี|กิโลกรัม|กก\.|%))?)/

/** A key point in parts, its figures marked: printed and shown bold and underlined, so a limit or a deadline is not missed */
export const termsParts = (point: string) =>
  point
    .split(FIGURE)
    .filter(Boolean)
    .map((text) => ({ text, figure: FIGURE.test(text) && /^\d/.test(text) }))

/** The summary's points, a line each, without bullets typed before them */
export const termsPoints = (summary: unknown) =>
  String(summary ?? '')
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[-•*·]|\d+[.)])\s*/, '').trim())
    .filter(Boolean)
